/**
 * §1.2 — the cognitive dashboard.
 *
 * The Operational Health widget on top, the three pillar buttons under it, and
 * the project list filtered by whatever segment the widget currently has
 * selected. §1.2.1 pre-filters that selection to `active_fieldwork` on load,
 * which is why the list below opens on the work that is actually running
 * rather than on every lead ever entered.
 */
import { useEffect, useState } from 'react';
import type { PillarButton, PillarCode, Project } from '../lib/api';
import { api } from '../lib/api';
import { OperationalHealthWidget } from './OperationalHealth';
import { PillarButtons } from './PillarButtons';
import { ProjectTable } from './ProjectTable';
import { statusLabel } from '../lib/status';

interface Props {
  scope: PillarCode | null;
  projects: Project[];
  pillars: PillarButton[];
  onOpenPillar: (code: string) => void;
}

export function Dashboard({ scope, projects, pillars, onOpenPillar }: Props): JSX.Element {
  const [segment, setSegment] = useState<string | null>(null);

  // The filter is a view over the already-loaded list, not a second request:
  // the widget and the table have to agree, and two independent reads of a
  // moving database do not.
  const filtered = segment === null ? projects : projects.filter((p) => p.status === segment);

  return (
    <div className="flex flex-col gap-5">
      <OperationalHealthWidget scope={scope} selected={segment} onSelect={setSegment} />

      <PillarButtons pillars={pillars} onOpen={onOpenPillar} />

      <section aria-labelledby="dashboard-projects">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="dashboard-projects" className="text-h2 font-semibold text-text-primary">
            {segment ? statusLabel(segment) : 'All projects in scope'}
          </h2>
          <p className="text-micro text-text-secondary">
            {filtered.length} of {projects.length} shown
            {segment ? ' · filtered by the health bar above' : ''}
          </p>
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <p className="text-body font-medium text-text-primary">
              {projects.length === 0
                ? 'No projects in this scope yet'
                : `No projects are in ${statusLabel(segment ?? '').toLowerCase()}`}
            </p>
            {projects.length > 0 ? (
              <p className="mt-1 text-body">Select another segment or clear the filter above.</p>
            ) : null}
          </div>
        ) : (
          <ProjectTable projects={filtered} />
        )}
      </section>
    </div>
  );
}

/** Loads the §1.3 button data once per session and keeps it for every screen. */
export function usePillarButtons(enabled: boolean): PillarButton[] {
  const [pillars, setPillars] = useState<PillarButton[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void (async () => {
      try {
        const { pillars: next } = await api.pillarButtons();
        if (live) setPillars(next);
      } catch {
        if (live) setPillars([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [enabled]);
  return pillars;
}
